import { useState, useCallback, useMemo, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { getGuestToken, setGuestToken, hasGuestToken, clearGuestToken } from './guestToken';
import { GUEST_GENERATION_LIMIT } from './constants';
import { UserContext } from './userContextDef';

export function UserProvider({ children }) {
  const { isAuthenticated, isLoading: auth0Loading, user: auth0User, getAccessTokenSilently } = useAuth0();
  const location = useLocation();
  const [generationsUsed, setGenerationsUsed] = useState(0);
  const [generationsLimit, setGenerationsLimit] = useState(GUEST_GENERATION_LIMIT);
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [guestInitialized, setGuestInitialized] = useState(false);
  const [isLimitsLoading, setIsLimitsLoading] = useState(hasGuestToken());
  const [isCreditsLoading, setIsCreditsLoading] = useState(false);

  // Credit and subscription state
  const [credits, setCredits] = useState(0);
  const [monthlyCredits, setMonthlyCredits] = useState(0);
  const [topupCredits, setTopupCredits] = useState(0);
  const [monthlyCreditsExpiresAt, setMonthlyCreditsExpiresAt] = useState(null);
  const [downloadCost, setDownloadCost] = useState(5);
  const [subscriptionTier, setSubscriptionTier] = useState(null);
  const [subscriptionExpiresAt, setSubscriptionExpiresAt] = useState(null);
  const [scheduledCancellation, setScheduledCancellation] = useState(false);
  const [cancelledAt, setCancelledAt] = useState(null);
  const [backendId, setBackendId] = useState(null);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isUserInfoLoading, setIsUserInfoLoading] = useState(false);
  const [showPostSignupModal, setShowPostSignupModal] = useState(() => {
    // Check sessionStorage on mount for post-signup flag
    return sessionStorage.getItem('aura_just_signed_up') === 'true';
  });

  // Initialize guest session if needed (called once on mount for guests)
  const initializeGuestSession = useCallback(async () => {
    // Skip if user is authenticated or already have guest token or already initialized
    if (isAuthenticated || hasGuestToken() || guestInitialized) return;

    setGuestInitialized(true);

    try {
      const response = await fetch('/api/v1/users/guest', {
        method: 'POST',
      });

      if (response.ok) {
        const data = await response.json();
        setGuestToken(data.guestToken);
        // Fetch limits immediately after guest session is created
        fetchLimits(null);
      }
    } catch (error) {
      console.error('Failed to create guest session:', error);
    }
  }, [isAuthenticated, guestInitialized]);

  // Fetch user limits from backend
  const fetchLimits = useCallback(async (tokenArg) => {
    try {
      const headers = {};
      const token = tokenArg || (isAuthenticated ? await getAccessTokenSilently() : null);

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else {
        const guestToken = getGuestToken();
        if (guestToken) {
          headers['X-Guest-Token'] = guestToken;
        }
      }

      const response = await fetch('/api/v1/users/me/limits', { headers });

      if (response.ok) {
        const data = await response.json();
        setGenerationsUsed(data.generationsUsed || 0);
        setGenerationsLimit(data.generationsLimit ?? (isAuthenticated ? 10 : GUEST_GENERATION_LIMIT));
        setIsUnlimited(data.isUnlimited || false);
      } else if (response.status === 401 && !isAuthenticated) {
        // Guest token invalid. Clear it so a new session can be initialized.
        console.warn('Guest token expired or invalid. Clearing session.');
        clearGuestToken();
        // This will trigger initializeGuestSession on next render/effect
        setGuestInitialized(false);
      }
    } catch (error) {
      console.error('Failed to fetch limits:', error);
    } finally {
      setIsLimitsLoading(false);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Fetch user credits from backend
  const fetchCredits = useCallback(async (tokenArg) => {
    // Only fetch credits for authenticated users
    if (!isAuthenticated) {
      return;
    }

    try {
      const token = tokenArg || await getAccessTokenSilently();
      const response = await fetch('/api/v1/users/me/credits', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCredits(data.balance || 0);
        setMonthlyCredits(data.monthlyCredits || 0);
        setTopupCredits(data.topupCredits || 0);
        setMonthlyCreditsExpiresAt(data.monthlyCreditsExpiresAt || null);
        setDownloadCost(data.downloadCost || 5);
      }
    } catch (error) {
      console.error('Failed to fetch credits:', error);
    } finally {
      setIsCreditsLoading(false);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Fetch user info (including subscription) from backend
  const fetchUserInfo = useCallback(async (token) => {
    if (!isAuthenticated || !token) {
      return null;
    }

    try {
      // Fetch basic user info
      const userResponse = await fetch('/api/v1/users/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (userResponse.ok) {
        const data = await userResponse.json();
        setSubscriptionTier(data.subscriptionTier);
        setSubscriptionExpiresAt(data.subscriptionExpiresAt);
        setBackendId(data.id);
        setIsEmailVerified(data.emailVerified);
        return data;
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      return null;
    }
  }, [isAuthenticated]);

  // Fetch detailed subscription info
  const fetchSubscriptionDetails = useCallback(async (token) => {
    if (!isAuthenticated || !token) return;
    try {
      const subResponse = await fetch('/api/v1/users/me/subscription', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (subResponse.ok) {
        const data = await subResponse.json();
        console.log('[DEBUG] Subscription API response:', data);
        setScheduledCancellation(data.scheduledCancellation || false);
        setCancelledAt(data.cancelledAt || null);
      }
    } catch (error) {
      console.error('Failed to fetch subscription details:', error);
    }
  }, [isAuthenticated]);

  // Cancel subscription function
  const cancelSubscription = useCallback(async () => {
    if (!isAuthenticated) return false;

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch('/api/v1/users/me/subscription/cancel', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setScheduledCancellation(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      return false;
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Claim guest account
  const claimGuestAccount = useCallback(async (token) => {
    const guestToken = getGuestToken();
    if (!isAuthenticated || !token || !guestToken) return false;

    try {
      const response = await fetch('/api/v1/users/claim', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ guestToken })
      });

      if (response.ok) {
        console.log('Guest account claimed successfully');
        setGuestToken(null); // Clear guest token after successful claim
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to claim guest account:', error);
      return false;
    }
  }, [isAuthenticated]);

  const dismissPostSignupModal = useCallback(() => {
    setShowPostSignupModal(false);
    sessionStorage.removeItem('aura_just_signed_up');
  }, []);

  const resendVerificationEmail = useCallback(async () => {
    if (!isAuthenticated) return false;
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch('/api/v1/users/me/resend-verification', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.ok;
    } catch (error) {
      console.error('Failed to resend verification email:', error);
      return false;
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Request password reset email
  const requestPasswordResetEmail = useCallback(async () => {
    if (!isAuthenticated) return false;
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch('/api/v1/users/me/password-reset-email', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.ok;
    } catch (error) {
      console.error('Failed to request password reset email:', error);
      return false;
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Fetch user data when authenticated
  useEffect(() => {
    let isMounted = true;

    async function initializeUser() {
      if (isAuthenticated && !auth0Loading) {
        try {
          // 1. Get token first - this is the "source of truth" and triggers backend JIT provisioning
          const token = await getAccessTokenSilently();
          if (!isMounted) return;

          // 2. Set loading states
          setIsCreditsLoading(true);
          setIsLimitsLoading(true);
          setIsUserInfoLoading(true);

          // 3. If we have a guest token, claim it first
          // This ensures guest data is merged before we fetch user info/history
          if (hasGuestToken()) {
            await claimGuestAccount(token);
          }

          // 4. Fetch user data in parallel once identity is established
          // fetchUserInfo will establish the userId if it's the first time
          await Promise.all([
            fetchUserInfo(token),
            fetchCredits(token),
            fetchLimits(token),
            fetchSubscriptionDetails(token)
          ]);

        } catch (error) {
          console.error('Error initializing authenticated user:', error);
        } finally {
          if (isMounted) {
            setIsCreditsLoading(false);
            setIsLimitsLoading(false);
            setIsUserInfoLoading(false);
          }
        }
      }
    }

    initializeUser();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, auth0Loading, getAccessTokenSilently, fetchUserInfo, fetchCredits, fetchLimits, fetchSubscriptionDetails, claimGuestAccount]);

  // Initialize on first render for guests (non-effect pattern)
  if (!auth0Loading && !isAuthenticated && !hasGuestToken() && !guestInitialized) {
    initializeGuestSession();
  }

  // Effect to fetch limits for guest users who already have a token
  useEffect(() => {
    // If it's a returning guest (has token) and we haven't fetched limits yet (isLimitsLoading is true from initial state)
    // Also wait for Auth0 to finish loading so we don't fetch as guest if we're about to be authenticated
    if (!auth0Loading && !isAuthenticated && hasGuestToken() && isLimitsLoading && !guestInitialized) {
      fetchLimits();
    }
  }, [auth0Loading, isAuthenticated, fetchLimits, isLimitsLoading, guestInitialized]);

  // REACTIVE SPA LOGIC: Detect post-signup flag on route changes
  useEffect(() => {
    const justSignedUp = sessionStorage.getItem('aura_just_signed_up') === 'true';
    if (justSignedUp && isAuthenticated) {
      setShowPostSignupModal(true);
    }
  }, [location, isAuthenticated]);

  // REACTIVE SPA LOGIC: Listen for global Paddle checkout events
  useEffect(() => {
    const handleCheckoutComplete = () => {
      console.log('[UserContext] Detected Paddle checkout completion, refreshing user data...');
      // Add a small delay to ensure backend has processed the webhook
      setTimeout(() => {
        fetchCredits();
        fetchUserInfo(); // Also refresh subscription status
      }, 2000);
    };

    window.addEventListener('paddle:checkout-completed', handleCheckoutComplete);
    return () => {
      window.removeEventListener('paddle:checkout-completed', handleCheckoutComplete);
    };
  }, [fetchCredits, fetchUserInfo]);

  // Increment generations used (call after successful generation)
  const incrementGenerationsUsed = useCallback(() => {
    setGenerationsUsed(prev => prev + 1);
  }, []);

  // Compute derived state
  const userState = useMemo(() => {
    const isGuest = !isAuthenticated && hasGuestToken();
    const hasProSubscription = subscriptionTier === 'pro' &&
      subscriptionExpiresAt &&
      new Date(subscriptionExpiresAt) > new Date();

    return {
      isLoading: auth0Loading || isUserInfoLoading,
      isUserLoading: isUserInfoLoading,
      isGuest,
      isAuthenticated,
      user: isAuthenticated ? { ...auth0User, email_verified: isEmailVerified || auth0User?.email_verified } : null,
      backendId,
      generationsUsed,
      generationsLimit,
      isUnlimited,
      canGenerate: isUnlimited || generationsUsed < generationsLimit,
      remaining: isUnlimited ? 9999 : Math.max(0, generationsLimit - generationsUsed),
      // Credit and subscription info
      credits,
      monthlyCredits,
      topupCredits,
      monthlyCreditsExpiresAt,
      downloadCost,
      canDownload: credits >= downloadCost,
      downloadsAvailable: downloadCost > 0 ? Math.floor(credits / downloadCost) : 0,
      subscriptionTier,
      subscriptionExpiresAt,
      scheduledCancellation,
      cancelledAt,
      hasProSubscription,
      isLimitsLoading,
      isCreditsLoading,
      showPostSignupModal,
    };
  }, [auth0Loading, isAuthenticated, auth0User, backendId, generationsUsed, generationsLimit, isUnlimited, credits, monthlyCredits, topupCredits, monthlyCreditsExpiresAt, downloadCost, subscriptionTier, subscriptionExpiresAt, scheduledCancellation, cancelledAt, isLimitsLoading, isCreditsLoading, showPostSignupModal]);

  const value = useMemo(() => ({
    ...userState,
    incrementGenerationsUsed,
    cancelSubscription,
    refetchLimits: fetchLimits,
    refetchCredits: fetchCredits,
    refetchUserInfo: fetchUserInfo,
    dismissPostSignupModal,
    resendVerificationEmail,
    requestPasswordResetEmail,
  }), [userState, incrementGenerationsUsed, cancelSubscription, fetchLimits, fetchCredits, fetchUserInfo, dismissPostSignupModal, resendVerificationEmail, requestPasswordResetEmail]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}
